import { useEffect, useState } from "react";
import { useDebounce, formatCurrency, ROLE } from "../../../../helpers/utils";
import { svg } from "../../../../Assets/svg";
import { RootState, useAppDispatch, useAppSelector } from "../../../../store";
import { useNavigate, useParams } from "react-router-dom";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../../helpers/permissionModule";
import FullScreenLoader from "../../../Components/FullScreenLoader";
import NoDataMessage from "../../../Components/NoData";
import InfiniteScroll from "react-infinite-scroll-component";
import { Dropdown, Menu, Spin, Table, TableColumnsType, Tooltip } from "antd";
import dayjs from "dayjs";
import { OrderStatusEnum, OrderStatusLabels } from "../../Orders/orderTypes";
import { DeleteOrderAPI, GetOrdersListAPI } from "../../../Redux/orders";
import CommonDeleteModal from "../../../Components/Modals/CommonDeleteModal";
import ViewProfitDataModal from "../../Orders/Components/Modals/ViewProfitDataModal";
import { getDecryptedUserData } from "../../../../helpers/bycrypt";

const ClientOrders = ({ clientId }: { clientId: string }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const USER_PERMISSIONS = usePermissionsChecker(PagePermissionNameData.ORDERS);
  const { getOrdersLoading, deleteOrderLoading, authLoading } = useAppSelector(
    (state: RootState) => state.loading,
  );

  const user = getDecryptedUserData();

  const { orderListData, ordersCount, CreatedByData, CompanyData } =
    useAppSelector((state: RootState) => state.orders);

  const loading = false;
  const totalCount = 0;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(25);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 300);
  const [hasMore, setHasMore] = useState(true);
  const [startDate, setStartDate] = useState<any>("");
  const [endDate, setEndDate] = useState<any>("");

  const [isDeleteId, setIsDeleteId] = useState<any>(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);

  const [profitData, setProfitData] = useState<any>(null);
  const [profitModal, setProfitModal] = useState(false);

  const [sorterState, setSorterState] = useState<{
    field: string | null;
    order: "ascend" | "descend" | null;
  }>({
    field: null,
    order: null,
  });

  const payyload = {
    page: currentPage,
    limit: limit,
    search: debouncedSearch,
    customer_id: clientId,
    sortBy: sorterState.field,
    sortOrder:
      sorterState?.order === "ascend"
        ? "asc"
        : sorterState?.order === "descend"
        ? "desc"
        : null,
  };
  useEffect(() => {
    // let sortBy = sorterState.field;
    // let order =
    //   sorterState.order === "ascend" ? "asc" : sorterState.order === "descend" ? "desc" : null;
    if (clientId) {
      dispatch(GetOrdersListAPI(payyload, setHasMore));
    }
  }, [dispatch, currentPage, limit, debouncedSearch, sorterState, clientId]);

  const searchBarClean = () => {
    setSearch("");
  };

  const searchBarHandler = (e: any) => {
    setSearch(e.target.value);
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 991.98);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 991.98);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter.order) {
      setSorterState({ field: sorter.field, order: sorter.order }); // Store sorter state
    } else {
      setSorterState({ field: null, order: null });
    }
    setCurrentPage(1);
  };

  const statusFindClass = (status: string) => {
    switch (status) {
      case OrderStatusEnum.ORDER_CREATED:
        return "statusDraft";
      case OrderStatusEnum.PRODUCTION_PLANNING:
        return "designApprovedStatus";
      case OrderStatusEnum.PRODUCTION_STARTED:
        return "approvedStatus";
      case OrderStatusEnum.READY_FOR_DISPATCH:
        return "sentStatus";
      case OrderStatusEnum.DISPATCHED:
        return "savedStatus";
      case OrderStatusEnum.DELIVERED:
        return "deliveredStatus";
      case OrderStatusEnum.UNDER_INSTALLATION:
        return "underInstallationStatus";
      case OrderStatusEnum.INSTALLED:
        return "designPendingStatus";
      case OrderStatusEnum.CLOSED:
        return "closedStatus";
      default:
        return "statusDraft"; // Fallback
    }
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Order Id",
      dataIndex: "order_id",
      key: "order_id",
      sorter: true,
      sortOrder: sorterState.field === "order_id" ? sorterState.order : null, // Keep arrow selected
      render: (order_id: any) => (order_id ? `${order_id}` : "-"),
    },
    {
      title: "Quote ID",
      dataIndex: "quote_id",
      key: "quote_id",
      sorter: true,
      sortOrder: sorterState.field === "quote_id" ? sorterState.order : null, // Keep arrow selected
      render: (quote_id: any, record: any) =>
        quote_id ? (
          <div
            className="text-decoration-underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                `/quotations/${record?.obj_quote_id}?tab=customer-details`,
                { state: { backPath: `/orders` } },
              );
            }}
          >
            {quote_id}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Company Name",
      dataIndex: "customer_snapshot.company_name",
      key: "customer_snapshot.company_name",
      sorter: true,
      sortOrder:
        sorterState.field === "customer_snapshot.company_name"
          ? sorterState.order
          : null, // Keep arrow selected
      render: (customer_snapshot: any, record: any) =>
        record?.customer_snapshot.company_name
          ? record?.customer_snapshot.company_name
          : "-",
    },
    {
      title: "Amount",
      dataIndex: "grand_total",
      key: "grand_total",
      sorter: true,
      sortOrder: sorterState.field === "grand_total" ? sorterState.order : null, // Keep arrow selected
      render: (grand_total: any, record: any) =>
        grand_total
          ? formatCurrency(
              grand_total,
              record?.currency || "GBP",
              record?.conversion_rate || 1,
            )
          : formatCurrency(
              0,
              record?.currency || "GBP",
              record?.conversion_rate || 1,
            ),
    },
    ...([ROLE.ADMIN, ROLE.SUB_ADMIN].includes(user?.role?.key)
      ? [
          {
            title: "Estimated Profit",
            dataIndex: "estimated_profit",
            key: "estimated_profit",
            width: 100,
            sorter: true,
            sortOrder: sorterState.field === "estimated_profit" ? sorterState.order : null,
            render: (_: any, record: any) => {
              const profit_data = record?.profit_data;
              const profit = profit_data?.estimated_profit || 0;
              return (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfitData({
                      ...profit_data,
                      display_quote_id: record?.display_quote_id,
                      vat_percentages: record?.vat_percentage,
                      conversion_rate: record?.conversion_rate,
                      currency: record?.currency,
                    });
                    setProfitModal(true);
                  }}
                >
                  {profit > 0 ? (
                    <span className="text-success fw-semibold">
                      {formatCurrency(
                        Number(profit) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : profit < 0 ? (
                    <span className="text-danger fw-semibold">
                      {formatCurrency(
                        Number(Math.abs(profit)) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : (
                    formatCurrency(
                      Number(0) || 0,
                      record?.currency,
                      record?.conversion_rate,
                    )
                  )}
                </div>
              );
            },
          },
          {
            title: "Actual Profit",
            dataIndex: "total_profit",
            key: "total_profit",
            width: 100,
            sorter: true,
            sortOrder: sorterState.field === "total_profit" ? sorterState.order : null,
            render: (_: any, record: any) => {
              const profit_data = record?.profit_data;
              const profit = profit_data?.total_profit || 0;
              return !profit_data?.is_order_created ? (
                <Tooltip
                  title={
                    "The actual profit calculation will be shown after the order is invoiced."
                  }
                >
                  <div className="d-flex justify-content-center">-</div>
                </Tooltip>
              ) : (
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfitData({
                      ...profit_data,
                      display_order_id: record?.order_id,
                      vat_percentages: record?.vat_percentage,
                      conversion_rate: record?.conversion_rate,
                      currency: record?.currency,
                    });
                    setProfitModal(true);
                  }}
                >
                  {profit > 0 ? (
                    <span className="text-success fw-semibold">
                      {formatCurrency(
                        Number(profit) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : profit < 0 ? (
                    <span className="text-danger fw-semibold">
                      {formatCurrency(
                        Number(Math.abs(profit)) || 0,
                        record?.currency,
                        record?.conversion_rate,
                      )}
                    </span>
                  ) : (
                    formatCurrency(
                      Number(0) || 0,
                      record?.currency,
                      record?.conversion_rate,
                    )
                  )}
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: "Next Payment Stage",
      dataIndex: "payment_milestones",
      key: "payment_milestones",
      // sorter: true,
      // sortOrder:
      //   sorterState.field === "payment_milestones" ? sorterState.order : null, // Keep arrow selected
      render: (payment_milestones: any, record: any) => {
        const findUnPaidMilestone = payment_milestones?.find(
          (elem: any) => elem?.paid_status === "pending" || !elem?.paid_status,
        );
        const finalAmount = `${findUnPaidMilestone?.milestone_name}`;
        return payment_milestones?.length > 0 ? (
          findUnPaidMilestone ? (
            finalAmount
          ) : (
            <span className="text-success fw-semibold">Paid</span>
          )
        ) : (
          "-"
        );
      },
    },
    {
      title: "Estimate / Actual Lead Time",
      dataIndex: "estimated_lead_time",
      key: "estimated_lead_time",
      width: 140,
      render: (_: any, record: any) => {
        const orderStatus = record?.order_status;
        const estimatedLeadTimeDays = record?.estimated_lead_time;
        const rawActualLeadTime = record?.actual_lead_time;

        if (estimatedLeadTimeDays == null && rawActualLeadTime == null) {
          return <span>-</span>;
        }

        const estimatedLeadTimeWeeks =
          estimatedLeadTimeDays != null
            ? Number((estimatedLeadTimeDays / 7).toFixed(1))
            : null;

        const actualLeadTimeWeeks =
          rawActualLeadTime != null
            ? Number((rawActualLeadTime / 7).toFixed(1))
            : 0;

        const isLeadTimeDispatched =
          [
            OrderStatusEnum.READY_FOR_DISPATCH,
            OrderStatusEnum.DISPATCHED,
            OrderStatusEnum.DELIVERED,
            OrderStatusEnum.UNDER_INSTALLATION,
            OrderStatusEnum.INSTALLED,
            OrderStatusEnum.CLOSED,
          ].includes(orderStatus) && rawActualLeadTime != null;

        if (estimatedLeadTimeWeeks == null) {
          return <span>-</span>;
        }

        if (!isLeadTimeDispatched) {
          return (
            <span className="fw-semibold text-primary">
              {estimatedLeadTimeWeeks.toFixed(1)}{" "}
              {estimatedLeadTimeWeeks === 1 ? "Week" : "Weeks"}
            </span>
          );
        }

        const diffWeeks = Number(
          (actualLeadTimeWeeks - estimatedLeadTimeWeeks).toFixed(1),
        );
        const leadTimeExceeded = diffWeeks > 0;

        return (
          <div className="d-flex align-items-center gap-1 flex-wrap">
            <span className="fw-semibold text-primary">
              {estimatedLeadTimeWeeks.toFixed(1)} /{" "}
              {actualLeadTimeWeeks.toFixed(1)} Weeks
            </span>
            <span
              className={`fw-bold ${
                leadTimeExceeded ? "text-danger" : "text-success"
              }`}
            >
              ({leadTimeExceeded ? `+${diffWeeks}` : `${diffWeeks}`}{" "}
              {Math.abs(diffWeeks) === 1 ? "Week" : "Weeks"})
            </span>
          </div>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "order_created_by",
      key: "order_created_by",
      sorter: true,
      sortOrder:
        sorterState.field === "order_created_by" ? sorterState.order : null, // Keep arrow selected
      render: (order_created_by: any) => {
        const displayName = `${order_created_by?.first_name} ${order_created_by?.last_name}`;
        return displayName ? displayName : "-";
      },
    },
    {
      title: "Created On",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: sorterState.field === "created_at" ? sorterState.order : null, // Keep arrow selected
      render: (created_at: any) =>
        created_at ? dayjs(created_at).format("DD MMM YYYY, hh:mm A") : "-",
    },
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      sorter: true,
      sortOrder:
        sorterState.field === "order_status" ? sorterState.order : null, // Keep arrow selected
      render: (order_status: any, record: any) => {
        const statusLabel =
          OrderStatusLabels[order_status as OrderStatusEnum] || order_status;
        return (
          <span
            className={
              "d-inline-flex align-items-center billStatus " +
              statusFindClass(order_status)
            }
          >
            {svg.dotIcon}
            {statusLabel == "Delivered" && !record?.ProductionPlanning
              ? "Ready For Installation"
              : statusLabel}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="d-flex" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            overlay={() => MoreOptionsMenu(record)}
            trigger={["click"]}
            placement="bottomRight"
            className="cursor-pointer"
            overlayClassName="moreOptionDropdown"
          >
            <span className="svg-28 svg-outline-primary">
              {svg?.tableMoreOption}
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  const handleDeleteOrderData = () => {
    const payload = {
      id: isDeleteId,
    };
    const onDeleteSuceess = () => {
      setIsDeleteId(null);
      setIsDeleteModal(false);
    };
    dispatch(DeleteOrderAPI(payload, onDeleteSuceess));
  };

  const MoreOptionsMenu = (data: any) => {
    return (
      <Menu>
        <Menu.Item
          key="1"
          onClick={() => {
            navigate(`/orders/${data?._id}`);
          }}
        >
          <div className="text-black d-flex align-items-center gap-2 svg-20">
            View
          </div>
        </Menu.Item>

        {USER_PERMISSIONS?.REMOVE &&
        data?.order_status == OrderStatusEnum?.ORDER_CREATED ? (
          <Menu.Item
            key="2"
            onClick={({ domEvent }: any) => {
              domEvent?.stopPropagation?.();
              setIsDeleteId(data?._id);
              setIsDeleteModal(true);
            }}
          >
            <div className="text-danger d-flex align-items-center gap-2 svg-20">
              Delete
            </div>
          </Menu.Item>
        ) : (
          ""
        )}
      </Menu>
    );
  };

  return (
    <div className="mb-md-3 mb-2 d-flex flex-column gap-md-2 gap-0">
      <div className="d-flex flex-md-nowrap flex-wrap gap-md-3 gap-2 justify-content-between align-items-center">
        <h2 className="text-black fs-36 fw-bolder page-title mb-0 order-sm-1 order-1">
          Orders
          <span>
            {currentPage === 1 && loading
              ? ""
              : totalCount
              ? `(${totalCount})`
              : ""}
          </span>
        </h2>
        <div
          className={`d-flex flex-md-nowrap flex-wrap gap-sm-3 gap-2 justify-content-end align-items-center ms-auto order-sm-2 order-3 mobileFilter`}
        >
          <div
            className={`pageSearch mw-100 position-relative d-inline-block mt-sm-0 mt-2`}
          >
            <input
              type="text"
              value={search}
              className="form-control form-control-lg"
              onChange={searchBarHandler}
              placeholder="Search"
            />
            {search?.length ? (
              <span className="cursor-pointer" onClick={searchBarClean}>
                {svg?.searchClear}
              </span>
            ) : (
              <span>{svg?.search}</span>
            )}
          </div>
        </div>
      </div>
      <div className="card page-table-card page-table-device border-0 shadow rounded-4 flex-fill overflow-y-auto">
        <div className="card-body overflow-y-auto" id="scrollableDiv">
          {getOrdersLoading && currentPage == 1 ? (
            <FullScreenLoader />
          ) : orderListData?.length == 0 ? (
            <NoDataMessage
              icon={svg?.noOrderIcon}
              text="You do not have any orders yet."
            />
          ) : (
            <>
              <InfiniteScroll
                dataLength={orderListData.length}
                next={handleLoadMore}
                hasMore={hasMore}
                loader={
                  currentPage > 1 && (
                    <div className="text-center p-2">
                      <Spin />
                    </div>
                  )
                }
                scrollableTarget="scrollableDiv"
                height={"100%"}
                style={{ overflow: "auto" }}
              >
                {!isMobile ? (
                  /* --- DESKTOP VIEW: Table element only --- */
                  <Table
                    dataSource={orderListData}
                    scroll={{ x: "max-content" }}
                    pagination={false}
                    columns={columns}
                    onChange={handleTableChange}
                    rowKey={(record) => record._id}
                    className="custom-table"
                    bordered
                    onRow={(record) => ({
                      onClick: () =>
                        navigate(`/orders/${record?._id}`, {
                          state: {
                            backPath: `/clients-distributors/${clientId}`,
                            backTabPath: "Orders",
                          },
                        }),
                    })}
                  />
                ) : (
                  /* --- MOBILE VIEW: Card Layout as per Quotations style --- */
                  <div className="d-flex flex-column gap-3 px-3">
                    {orderListData.map((record: any) => (
                      <div
                        key={record._id}
                        className="card mobile-table-card mobile-font-s rounded-4 p-3"
                        onClick={() =>
                          navigate(`/orders/${record?._id}`, {
                            state: {
                              backPath: `/clients-distributors/${clientId}`,
                              backTabPath: "Orders",
                            },
                          })
                        }
                      >
                        {/* Card Header: Order ID & Status/Action */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="fw-bold fs-5">
                            #{record.order_id || "-"}
                          </span>
                          <div className="d-flex align-items-center gap-2">
                            <span
                              className={`billStatus ${statusFindClass(
                                record.order_status,
                              )} d-inline-flex align-items-center`}
                            >
                              {svg.dotIcon}
                              {OrderStatusLabels[
                                record.order_status as OrderStatusEnum
                              ] || record.order_status}
                            </span>
                            <div onClick={(e) => e.stopPropagation()}>
                              <Dropdown
                                overlay={() => MoreOptionsMenu(record)}
                                trigger={["click"]}
                                placement="bottomRight"
                              >
                                <span className="cursor-pointer">
                                  {svg?.tableMobileOption}
                                </span>
                              </Dropdown>
                            </div>
                          </div>
                        </div>

                        {/* Subtitle: Company Name */}
                        <h6 className="fw-bold fs-5 mb-2">
                          {record?.customer_snapshot?.company_name || "-"}
                        </h6>

                        {/* Body: Order Details */}
                        <div className="mobile-card-details fs-6 fs-6-mob">
                          <div className="row gy-1">
                            <div className="col-12 col-md-6">
                              <div className="row">
                                <div className="col-sm-3 col-4 text-secondary fw-medium">
                                  Quote ID:
                                </div>
                                <div className="col-sm-9 col-8">
                                  <span
                                    className="text-decoration-underline text-primary cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(
                                        `/quotations/${record?.obj_quote_id}?tab=customer-details`,
                                      );
                                    }}
                                  >
                                    {record.quote_id || "-"}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="row">
                                <div className="col-sm-3 col-4 text-secondary fw-medium">
                                  Est.Profit:
                                </div>
                                <div className="col-sm-9 col-8">
                                  <div
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setProfitData({
                                        ...record?.profit_data,
                                        display_quote_id:
                                          record?.display_quote_id,
                                        vat_percentages: record?.vat_percentage,
                                        conversion_rate:
                                          record?.conversion_rate,
                                        currency: record?.currency,
                                      });
                                      setProfitModal(true);
                                    }}
                                  >
                                    {record?.profit_data?.estimated_profit >
                                    0 ? (
                                      <span className="text-success fw-semibold">
                                        {formatCurrency(
                                          Number(
                                            record?.profit_data
                                              ?.estimated_profit,
                                          ) || 0,
                                          record?.currency,
                                          record?.conversion_rate,
                                        )}
                                      </span>
                                    ) : record?.profit_data?.estimated_profit <
                                      0 ? (
                                      <span className="text-danger fw-semibold">
                                        {formatCurrency(
                                          Number(
                                            Math.abs(
                                              record?.profit_data
                                                ?.estimated_profit,
                                            ),
                                          ) || 0,
                                          record?.currency,
                                          record?.conversion_rate,
                                        )}
                                      </span>
                                    ) : (
                                      formatCurrency(
                                        Number(0) || 0,
                                        record?.currency,
                                        record?.conversion_rate,
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="row">
                                <div className="col-sm-3 col-4 text-secondary fw-medium">
                                  Actual Profit:
                                </div>
                                <div className="col-sm-9 col-8">
                                  {!record?.profit_data?.is_order_created ? (
                                    <Tooltip
                                      title={
                                        "The actual profit calculation will be shown after the order is invoiced."
                                      }
                                    >
                                      <div className="d-flex justify-content-start">
                                        -
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    <div
                                      className="cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setProfitData({
                                          ...record?.profit_data,
                                          display_order_id: record?.order_id,
                                          vat_percentages:
                                            record?.vat_percentage,
                                          conversion_rate:
                                            record?.conversion_rate,
                                          currency: record?.currency,
                                        });
                                        setProfitModal(true);
                                      }}
                                    >
                                      {record?.profit_data?.total_profit > 0 ? (
                                        <span className="text-success fw-semibold">
                                          {formatCurrency(
                                            Number(
                                              record?.profit_data?.total_profit,
                                            ) || 0,
                                            record?.currency,
                                            record?.conversion_rate,
                                          )}
                                        </span>
                                      ) : record?.profit_data?.total_profit <
                                        0 ? (
                                        <span className="text-danger fw-semibold">
                                          {formatCurrency(
                                            Number(
                                              Math.abs(
                                                record?.profit_data
                                                  ?.total_profit,
                                              ),
                                            ) || 0,
                                            record?.currency,
                                            record?.conversion_rate,
                                          )}
                                        </span>
                                      ) : (
                                        formatCurrency(
                                          Number(0) || 0,
                                          record?.currency,
                                          record?.conversion_rate,
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="row">
                                <div className="col-sm-3 col-4 text-secondary fw-medium">
                                  Next Payment Stage:
                                </div>
                                <div className="col-sm-9 col-8">
                                  {(() => {
                                    const payment_milestones =
                                      record.payment_milestones;
                                    const findUnPaidMilestone =
                                      payment_milestones?.find(
                                        (elem: any) =>
                                          elem?.paid_status === "pending" ||
                                          !elem?.paid_status,
                                      );
                                    const finalAmount = `${findUnPaidMilestone?.milestone_name}`;
                                    return payment_milestones?.length > 0 ? (
                                      findUnPaidMilestone ? (
                                        finalAmount
                                      ) : (
                                        <span className="text-success fw-semibold">
                                          Paid
                                        </span>
                                      )
                                    ) : (
                                      "-"
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="row">
                                <div className="col-sm-3 col-4 text-secondary fw-medium">
                                  Created by:
                                </div>
                                <div className="col-sm-9 col-8">
                                  {record.order_created_by
                                    ? `${record.order_created_by.first_name} ${record.order_created_by.last_name}`
                                    : "-"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="row">
                                <div className="col-sm-3 col-4 text-secondary fw-medium">
                                  Created on:
                                </div>
                                <div className="col-sm-9 col-8">
                                  {record.created_at
                                    ? dayjs(record.created_at).format(
                                        "DD MMM YYYY, hh:mm A",
                                      )
                                    : "-"}
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="row">
                                <div className="col-sm-3 col-4 text-secondary fw-medium">
                                  Lead Time:
                                </div>
                                <div className="col-sm-9 col-8">
                                  {(() => {
                                    const orderStatus = record?.order_status;
                                    const estimatedLeadTimeDays =
                                      record?.estimated_lead_time;
                                    const rawActualLeadTime =
                                      record?.actual_lead_time;

                                    if (
                                      estimatedLeadTimeDays == null &&
                                      rawActualLeadTime == null
                                    ) {
                                      return <span>-</span>;
                                    }

                                    const estimatedLeadTimeWeeks =
                                      estimatedLeadTimeDays != null
                                        ? Number(
                                            (
                                              estimatedLeadTimeDays / 7
                                            ).toFixed(1),
                                          )
                                        : null;

                                    const actualLeadTimeWeeks =
                                      rawActualLeadTime != null
                                        ? Number(
                                            (rawActualLeadTime / 7).toFixed(1),
                                          )
                                        : 0;

                                    const isLeadTimeDispatched =
                                      [
                                        OrderStatusEnum.READY_FOR_DISPATCH,
                                        OrderStatusEnum.DISPATCHED,
                                        OrderStatusEnum.DELIVERED,
                                        OrderStatusEnum.UNDER_INSTALLATION,
                                        OrderStatusEnum.INSTALLED,
                                        OrderStatusEnum.CLOSED,
                                      ].includes(orderStatus) &&
                                      rawActualLeadTime != null;

                                    if (estimatedLeadTimeWeeks == null) {
                                      return <span>-</span>;
                                    }

                                    if (!isLeadTimeDispatched) {
                                      return (
                                        <span className="fw-semibold text-primary">
                                          {estimatedLeadTimeWeeks.toFixed(1)}{" "}
                                          {estimatedLeadTimeWeeks === 1
                                            ? "Week"
                                            : "Weeks"}
                                        </span>
                                      );
                                    }

                                    const diffWeeks = Number(
                                      (
                                        actualLeadTimeWeeks -
                                        estimatedLeadTimeWeeks
                                      ).toFixed(1),
                                    );
                                    const leadTimeExceeded = diffWeeks > 0;

                                    return (
                                      <div className="d-flex align-items-center gap-1 flex-wrap">
                                        <span className="fw-semibold text-primary">
                                          {estimatedLeadTimeWeeks.toFixed(1)} /{" "}
                                          {actualLeadTimeWeeks.toFixed(1)} Weeks
                                        </span>
                                        <span
                                          className={`fw-bold ${
                                            leadTimeExceeded
                                              ? "text-danger"
                                              : "text-success"
                                          }`}
                                        >
                                          ({leadTimeExceeded
                                            ? `+${diffWeeks}`
                                            : `${diffWeeks}`}{" "}
                                          {Math.abs(diffWeeks) === 1
                                            ? "Week"
                                            : "Weeks"})
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Footer: Total Amount */}
                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                          <span className="fw-bold">Amount:</span>
                          <span className="fw-bold fs-5">
                            {formatCurrency(
                              record.grand_total || 0,
                              record?.currency || "GBP",
                              record?.conversion_rate || 1,
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </InfiniteScroll>
            </>
          )}
        </div>
      </div>
      {profitModal && (
        <ViewProfitDataModal
          visible={!!profitModal}
          setIsModalVisible={setProfitModal as any}
          onClose={() => {
            setProfitModal(false);
            setProfitData(null);
          }}
          profitData={profitData}
        />
      )}
      {isDeleteModal && (
        <CommonDeleteModal
          visible={isDeleteModal}
          setIsModalVisible={setIsDeleteModal}
          handleDelete={handleDeleteOrderData}
          title={"Remove Order, Are You Sure?"}
          description={
            "Are you sure you want to remove this order? All associated data will be permanently deleted."
          }
          setIsDeleteId={setIsDeleteId}
          isLoading={deleteOrderLoading}
          buttonName="REMOVE"
        />
      )}
    </div>
  );
};

export default ClientOrders;
